/**
 * Product template variant picker + variant media gallery.
 * Standalone module — no dependencies on a-main-product.
 */

if (!customElements.get('product-variant-selects')) {
	class ProductVariantSelects extends HTMLElement {
		constructor() {
			super()
			this.onChange = this.onChange.bind(this)
		}

		connectedCallback() {
			this.productId = this.dataset.productId
			this.sectionId = this.dataset.sectionId
			this.addEventListener('change', this.onChange)
		}

		onChange() {
			const variant = this.getSelectedVariant()
			if (!variant) return

			const selectedVariantScript = this.querySelector('[data-pdp-selected-variant]')
			if (selectedVariantScript) {
				selectedVariantScript.textContent = JSON.stringify(variant)
			}

			document.dispatchEvent(
				new CustomEvent('product:variant:changed', {
					detail: {
						sectionId: this.sectionId,
						productId: this.productId,
						variant,
					},
				})
			)
		}

		getSelectedVariant() {
			const variantsScript = this.querySelector('[data-pdp-product-variants]')
			if (!variantsScript) return null

			const variants = JSON.parse(variantsScript.textContent)
			const selectedOptions = []

			this.querySelectorAll('[data-pdp-variant-option]').forEach((fieldset) => {
				const checked = fieldset.querySelector('[data-pdp-variant-input]:checked')
				if (checked) selectedOptions.push(checked.value)
			})

			return (
				variants.find((variant) =>
					variant.options.every((option, index) => option === selectedOptions[index])
				) || null
			)
		}
	}

	customElements.define('product-variant-selects', ProductVariantSelects)
}

;(function initProductVariantGallery() {
	const SELECTORS = {
		section: '[data-product-variant-features="true"]',
		mediaGroup: '[data-product-single-media-group]',
		galleryRoot: '[data-pdp-gallery-root]',
		defaultGallery: '[data-pdp-default-gallery]',
		variantGallery: '[data-pdp-variant-gallery]',
		slider: '[data-product-single-media-slider]',
		thumbs: '[data-product-single-media-thumbs]',
		mediaWrapper: '[data-product-single-media-wrapper]',
		thumbLink: '.product-single__thumbnail-link',
		variantSelects: 'product-variant-selects',
	}

	const CLASSES = {
		mediaHidden: 'media--hidden',
		hasMediaActive: 'has-media-active',
		isMoving: 'is-moving',
	}

	function getFlickity() {
		return window.themeVendor?.Flickity
	}

	class ProductVariantGallery {
		constructor(section) {
			this.section = section
			this.sectionId = section.dataset.sectionId
			this.mediaGroup = section.querySelector(SELECTORS.mediaGroup)
			this.tallLayout = section.dataset.tallLayout === 'true'
			this.flkty = null
			this.flktyNav = null
			this.activeRoot = null
			this._onVariantChange = this._onVariantChange.bind(this)
			this._onResize = this._onResize.bind(this)

			if (!this.mediaGroup) return

			document.addEventListener('product:variant:changed', this._onVariantChange)
			document.addEventListener('theme:resize:width', this._onResize)

			requestAnimationFrame(() => {
				const variant = this._getSelectedVariant()
				const galleryRoot = variant
					? this._getGalleryRootForVariant(variant)
					: this.mediaGroup.querySelector(SELECTORS.defaultGallery)

				if (galleryRoot?.hasAttribute('data-pdp-default-gallery') && variant?.featured_media?.id) {
					this._activateGallery(galleryRoot, { slideToFeaturedMediaId: variant.featured_media.id })
				} else {
					this._activateGallery(galleryRoot, { slideToStart: true })
				}
			})
		}

		destroy() {
			document.removeEventListener('product:variant:changed', this._onVariantChange)
			document.removeEventListener('theme:resize:width', this._onResize)
			this._destroySlider()
		}

		_onResize() {
			if (!this.activeRoot) return

			if (this._shouldUseSlider()) {
				if (!this.flkty) {
					this._initSlider(this.activeRoot)
				}
			} else {
				this._destroySlider()
			}
		}

		_onVariantChange(event) {
			const { sectionId, variant } = event.detail || {}
			if (sectionId !== this.sectionId || !variant) return

			const galleryRoot = this._getGalleryRootForVariant(variant)

			if (galleryRoot?.hasAttribute('data-pdp-default-gallery') && variant.featured_media?.id) {
				this._activateGallery(galleryRoot, { slideToFeaturedMediaId: variant.featured_media.id })
			} else {
				this._activateGallery(galleryRoot, { slideToStart: true })
			}
		}

		_getSelectedVariant() {
			const variantSelects = this.section.querySelector(SELECTORS.variantSelects)
			return variantSelects?.getSelectedVariant?.() || null
		}

		_getGalleryRootForVariant(variant) {
			if (!variant?.id) {
				return this.mediaGroup.querySelector(SELECTORS.defaultGallery)
			}

			const variantGallery = this.mediaGroup.querySelector(
				`${SELECTORS.variantGallery}[data-pdp-variant-id="${variant.id}"]`
			)

			return variantGallery || this.mediaGroup.querySelector(SELECTORS.defaultGallery)
		}

		_shouldUseSlider() {
			const largeBreakpoint = window.theme?.sizes?.large || 1024
			return !this.tallLayout || window.innerWidth < largeBreakpoint
		}

		_setActiveGalleryRoot(galleryRoot) {
			this.mediaGroup.querySelectorAll(SELECTORS.galleryRoot).forEach((root) => {
				root.hidden = root !== galleryRoot
			})
		}

		_activateGallery(galleryRoot, { slideToStart = false, slideToFeaturedMediaId = null } = {}) {
			if (!galleryRoot) return

			const isSameRoot = this.activeRoot === galleryRoot

			if (!isSameRoot) {
				this._destroySlider()
				this._setActiveGalleryRoot(galleryRoot)
				this.activeRoot = galleryRoot

				if (this._shouldUseSlider()) {
					this._initSlider(galleryRoot)
				}
			}

			if (!this.flkty) return

			if (slideToFeaturedMediaId) {
				const slides = galleryRoot.querySelectorAll(SELECTORS.mediaWrapper)
				const index = Array.from(slides).findIndex(
					(slide) => slide.dataset.id == `${slideToFeaturedMediaId}`
				)
				this.flkty.select(index === -1 ? 0 : index, true)
				return
			}

			if (slideToStart) {
				this.flkty.select(0, true)
			}
		}

		_destroySlider() {
			const Flickity = getFlickity()
			if (!Flickity || !this.activeRoot) return

			const slider = this.activeRoot.querySelector(SELECTORS.slider)
			const thumbs = this.activeRoot.querySelector(SELECTORS.thumbs)

			Flickity.data(slider)?.destroy()
			Flickity.data(thumbs)?.destroy()

			this.flkty = null
			this.flktyNav = null
		}

		_initSlider(galleryRoot) {
			const Flickity = getFlickity()
			if (!Flickity) return

			const slider = galleryRoot.querySelector(SELECTORS.slider)
			const thumbs = galleryRoot.querySelector(SELECTORS.thumbs)
			const media = galleryRoot.querySelectorAll(SELECTORS.mediaWrapper)

			if (!slider || media.length <= 1) return

			this.flkty = new Flickity(slider, {
				wrapAround: true,
				pageDots: false,
				adaptiveHeight: true,
				on: {
					dragStart: () => {
						slider.classList.add(CLASSES.isMoving)
					},
					settle: () => {
						const currentSlide = this.flkty.selectedElement
						const mediaId = currentSlide?.getAttribute('data-media-id')
						if (mediaId) {
							this._switchMedia(galleryRoot, mediaId)
						}
						slider.classList.remove(CLASSES.isMoving)
					},
				},
			})

			if (thumbs) {
				this.flktyNav = new Flickity(thumbs, {
					asNavFor: slider,
					contain: true,
					pageDots: false,
					prevNextButtons: false,
					resize: true,
				})

				thumbs.querySelectorAll(SELECTORS.thumbLink).forEach((link) => {
					link.addEventListener('click', (event) => {
						event.preventDefault()
					})
				})
			}

			media.forEach((item) => {
				item.addEventListener('theme:media:play', () => {
					if (!this.flkty) return
					this.flkty.options.draggable = false
					this.flkty.updateDraggable()
					slider.classList.add(CLASSES.hasMediaActive)
				})

				item.addEventListener('theme:media:pause', () => {
					if (!this.flkty) return
					this.flkty.options.draggable = true
					this.flkty.updateDraggable()
					slider.classList.remove(CLASSES.hasMediaActive)
				})
			})
		}

		_switchMedia(galleryRoot, mediaId) {
			const mediaItems = galleryRoot.querySelectorAll(SELECTORS.mediaWrapper)
			const selectedMedia = galleryRoot.querySelector(
				`${SELECTORS.mediaWrapper}[data-media-id="${mediaId}"]`
			)

			if (!selectedMedia) return

			mediaItems.forEach((media) => {
				media.dispatchEvent(new CustomEvent('theme:media:hidden', { bubbles: true }))
				media.classList.add(CLASSES.mediaHidden)
			})

			selectedMedia.classList.remove(CLASSES.mediaHidden)
			selectedMedia.dispatchEvent(new CustomEvent('theme:media:visible', { bubbles: true }))
		}
	}

	const galleries = new WeakMap()

	function bootProductVariantGalleries() {
		document.querySelectorAll(SELECTORS.section).forEach((section) => {
			if (galleries.has(section)) return
			galleries.set(section, new ProductVariantGallery(section))
		})
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', bootProductVariantGalleries)
	} else {
		bootProductVariantGalleries()
	}

	document.addEventListener('shopify:section:load', (event) => {
		const section = event.target.querySelector?.(SELECTORS.section) || event.target
		if (section?.matches?.(SELECTORS.section) && !galleries.has(section)) {
			galleries.set(section, new ProductVariantGallery(section))
		}
	})

	document.addEventListener('shopify:section:unload', (event) => {
		const section = event.target.querySelector?.(SELECTORS.section) || event.target
		if (section?.matches?.(SELECTORS.section)) {
			galleries.get(section)?.destroy()
			galleries.delete(section)
		}
	})
})()
